import { useEffect, useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { fmtDate } from "@/lib/helpers";
import CatalogEditor from "@/components/CatalogEditor";
import UsersManager from "@/components/UsersManager";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Building2, Upload, FileJson, FileSpreadsheet, DatabaseBackup, Download, Trash2,
  Loader2, Lock, RotateCcw, FileUp, Mail, Stamp, Plane,
} from "lucide-react";

async function downloadBlob(path, fallbackName) {
  const res = await api.get(path, { responseType: "blob" });
  const disp = res.headers["content-disposition"] || "";
  const m = disp.match(/filename="?([^"]+)"?/);
  const name = m ? m[1] : fallbackName;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export default function Settings() {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState(null);
  const [backups, setBackups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const logoRef = useRef();
  const stampRef = useRef();
  const restoreRef = useRef();
  const excelRef = useRef();
  const [importing, setImporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingW, setSendingW] = useState(false);
  const [workerResult, setWorkerResult] = useState(null);
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });

  const sendWorkerReminders = async () => {
    setSendingW(true);
    try {
      await api.put("/settings", form);
      const r = await api.post("/alerts/send-worker-reminders");
      setWorkerResult(r.data);
      if (r.data.reason) toast.error(r.data.reason);
      else toast.success(`${r.data.sent} aviso(s) enviados · ${r.data.skipped} omitidos`);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudieron enviar los avisos"); }
    finally { setSendingW(false); }
  };

  const sendReminder = async () => {
    setSending(true);
    try {
      await api.put("/settings", form);
      const r = await api.post("/alerts/send-reminder");
      if (r.data.sent) toast.success(`Recordatorio enviado a ${r.data.to} (${r.data.alerts} avisos)`);
      else toast.error(r.data.reason || "No se envió");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el recordatorio");
    } finally { setSending(false); }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/import/excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importación completada: ${r.data.created} nuevos, ${r.data.updated} actualizados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo importar el Excel");
    } finally { setImporting(false); }
  };

  const load = () => {
    api.get("/settings").then((r) => setForm(r.data));
    api.get("/backups").then((r) => setBackups(r.data)).catch(() => {});
  };
  useEffect(load, []);

  if (!form) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });

  const onLogo = (e) => readImage(e, "logo", "El logo no puede superar 1,5 MB");
  const onStamp = (e) => readImage(e, "stamp", "La imagen del sello no puede superar 1,5 MB");
  const readImage = (e, key, msg) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) return toast.error(msg);
    const reader = new FileReader();
    reader.onload = () => set(key, reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Ajustes guardados correctamente");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const createBackup = async () => {
    setBusy(true);
    try {
      await api.post("/backups");
      toast.success("Copia de seguridad creada");
      load();
    } catch (e) { toast.error("No se pudo crear la copia"); }
    finally { setBusy(false); }
  };

  const restore = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const snap = JSON.parse(reader.result);
        await api.post("/backup/restore", snap);
        toast.success("Datos restaurados desde la copia");
        load();
      } catch { toast.error("Archivo de copia inválido"); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const delBackup = async (id) => {
    await api.delete(`/backups/${id}`);
    load();
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Ajustes y Copias de Seguridad</h1>
        <p className="text-muted-foreground text-sm mt-1">Configura tu empresa y gestiona los backups.</p>
      </div>

      {!isAdmin && (
        <Card className="p-4 flex items-center gap-3 bg-amber-50 border-amber-200 text-amber-800">
          <Lock className="h-5 w-5" />
          <p className="text-sm">Solo el administrador puede modificar ajustes y crear/restaurar copias. Puedes descargar backups.</p>
        </Card>
      )}

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5">
          <Building2 className="h-5 w-5 text-accent" />
          <h2 className="font-display text-lg font-bold">Datos de la Empresa</h2>
        </div>
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex flex-col items-center gap-3">
            <div className="h-28 w-28 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
              {form.logo ? <img src={form.logo} alt="logo" className="h-full w-full object-contain" />
                : <Building2 className="h-10 w-10 text-slate-300" />}
            </div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo}
              data-testid="upload-company-logo-input" />
            <Button variant="outline" size="sm" disabled={!isAdmin} onClick={() => logoRef.current?.click()}
              data-testid="upload-logo-button">
              <Upload className="h-4 w-4 mr-2" /> Subir logo
            </Button>
            <div className="h-20 w-36 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50 mt-2">
              {form.stamp ? <img src={form.stamp} alt="sello" className="h-full w-full object-contain" data-testid="company-stamp-preview" />
                : <Stamp className="h-8 w-8 text-slate-300" />}
            </div>
            <input ref={stampRef} type="file" accept="image/*" className="hidden" onChange={onStamp} data-testid="upload-company-stamp-input" />
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={!isAdmin} onClick={() => stampRef.current?.click()} data-testid="upload-stamp-button">
                <Stamp className="h-4 w-4 mr-2" /> Firma y sello
              </Button>
              {form.stamp && isAdmin && <Button variant="ghost" size="sm" onClick={() => set("stamp", "")} data-testid="remove-stamp-button">Quitar</Button>}
            </div>
            <p className="text-[11px] text-muted-foreground text-center max-w-[10rem]">Imagen de la firma del representante con el sello. Se imprime en la ficha de EPIs.</p>
          </div>
          <div className="flex-1 grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Nombre de la Empresa</Label>
              <Input value={form.company_name || ""} disabled={!isAdmin}
                onChange={(e) => set("company_name", e.target.value)} data-testid="company-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>CIF / NIF</Label>
              <Input value={form.cif || ""} disabled={!isAdmin} onChange={(e) => set("cif", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Sector / Actividad</Label>
              <Input value={form.sector || ""} disabled={!isAdmin} onChange={(e) => set("sector", e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Dirección</Label>
              <Input value={form.address || ""} disabled={!isAdmin} onChange={(e) => set("address", e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Representante de la empresa (nombre bajo la firma)</Label>
              <Input value={form.signer_name || ""} disabled={!isAdmin} placeholder="Ej. Marcos Fernández García"
                onChange={(e) => set("signer_name", e.target.value)} data-testid="company-signer-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Cargo del firmante</Label>
              <Input value={form.signer_role || ""} disabled={!isAdmin} placeholder="Ej. Administrador único"
                onChange={(e) => set("signer_role", e.target.value)} data-testid="company-signer-role-input" />
            </div>
            <div className="space-y-1.5">
              <Label>DNI del firmante (se imprime en los certificados)</Label>
              <Input value={form.signer_dni || ""} disabled={!isAdmin} placeholder="00000000A"
                onChange={(e) => set("signer_dni", e.target.value)} data-testid="company-signer-dni-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email de la gestoría (para enviar certificados)</Label>
              <Input type="email" value={form.accounting_email || ""} disabled={!isAdmin} placeholder="gestoria@ejemplo.es"
                onChange={(e) => set("accounting_email", e.target.value)} data-testid="accounting-email-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Texto de protección de datos (LOPD / RGPD)</Label>
              <Textarea rows={6} value={form.lopd_text || ""} disabled={!isAdmin}
                onChange={(e) => set("lopd_text", e.target.value)} data-testid="lopd-text-input" />
              <p className="text-xs text-muted-foreground">
                Se imprime al pie de todos los documentos que genera la app y se muestra en la pantalla de inicio.
              </p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email para recordatorios semanales</Label>
              <div className="flex gap-2">
                <Input type="email" placeholder="admin@empresa.es" value={form.alert_email || ""} disabled={!isAdmin}
                  onChange={(e) => set("alert_email", e.target.value)} data-testid="alert-email-input" />
                {isAdmin && (
                  <Button variant="outline" onClick={sendReminder} disabled={sending || !form.alert_email} data-testid="send-reminder-button">
                    {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} Enviar ahora
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Cada lunes se envía un resumen con las caducidades de los próximos 30 días. Guarda los cambios antes de enviar.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="flex items-center gap-1.5"><Plane className="h-3.5 w-3.5" /> Días de vacaciones al año (naturales)</Label>
              <Input type="number" min="1" max="365" className="w-28" value={form.vacation_days_per_year ?? 30} disabled={!isAdmin}
                onChange={(e) => set("vacation_days_per_year", Number(e.target.value))} data-testid="vacation-days-input" />
              <p className="text-xs text-muted-foreground">Cupo aplicado a todos los trabajadores para calcular el saldo en la página de Vacaciones.</p>
            </div>
            <div className="sm:col-span-2 space-y-2" data-testid="holidays-editor">
              <Label>Festivos propios (locales o de convenio)</Label>
              <p className="text-xs text-muted-foreground">
                Los festivos nacionales ya se marcan solos en el calendario. Añade aquí los de tu provincia o municipio.
              </p>
              <ul className="divide-y rounded-md border">
                {(form.holidays || []).length === 0 && <li className="p-2 text-xs text-muted-foreground">Sin festivos propios.</li>}
                {(form.holidays || []).map((h, i) => (
                  <li key={i} className="flex items-center gap-2 p-2 text-sm" data-testid={`holiday-row-${i}`}>
                    <span className="font-mono text-xs text-muted-foreground">{fmtDate(h.date)}</span>
                    <span className="min-w-0 flex-1 truncate">{h.name}</span>
                    {isAdmin && (
                      <button className="text-red-500 hover:text-red-600" data-testid={`holiday-delete-${i}`}
                        onClick={() => set("holidays", form.holidays.filter((_, k) => k !== i))}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {isAdmin && (
                <div className="flex flex-wrap gap-2">
                  <Input type="date" className="w-40" value={newHoliday.date} data-testid="holiday-date-input"
                    onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} />
                  <Input className="flex-1 min-w-[160px]" placeholder="Nombre del festivo" value={newHoliday.name}
                    data-testid="holiday-name-input" onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })} />
                  <Button variant="outline" data-testid="holiday-add-button"
                    onClick={() => {
                      if (!newHoliday.date || !newHoliday.name.trim()) return toast.error("Indica fecha y nombre del festivo");
                      set("holidays", [...(form.holidays || []), { date: newHoliday.date, name: newHoliday.name.trim() }]);
                      setNewHoliday({ date: "", name: "" });
                    }}>Añadir festivo</Button>
                </div>
              )}
            </div>
            <div className="sm:col-span-2 rounded-lg border p-3 space-y-3" data-testid="worker-alerts-block">
              <div className="flex flex-wrap items-center gap-3">
                <input type="checkbox" id="wa-enabled" className="h-4 w-4 accent-primary" checked={form.worker_alerts_enabled !== false} disabled={!isAdmin}
                  onChange={(e) => set("worker_alerts_enabled", e.target.checked)} data-testid="worker-alerts-checkbox" />
                <Label htmlFor="wa-enabled" className="flex-1">Avisos por email a los trabajadores (EPIs sin firmar, cursos PRL y reconocimientos próximos a caducar)</Label>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Avisar con</span>
                  <Input type="number" min="1" max="365" className="w-20 h-9" value={form.worker_alert_days ?? 30} disabled={!isAdmin}
                    onChange={(e) => set("worker_alert_days", Number(e.target.value))} data-testid="worker-alert-days-input" />
                  <span className="text-muted-foreground">días</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs text-muted-foreground flex-1">Se envían junto al resumen semanal (lunes 07:00) a cada trabajador con email en su ficha.</p>
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={sendWorkerReminders} disabled={sendingW} data-testid="send-worker-reminders-button">
                    {sendingW ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} Enviar avisos ahora
                  </Button>
                )}
              </div>
              {workerResult && (
                <ul className="text-xs divide-y border rounded-md" data-testid="worker-reminders-result">
                  {workerResult.results?.length === 0 && <li className="p-2 text-muted-foreground">Ningún trabajador tiene avisos pendientes.</li>}
                  {workerResult.results?.map((r, i) => (
                    <li key={i} className="p-2 flex justify-between gap-2"><span>{r.employee}</span>
                      <span className={r.ok ? "text-emerald-700" : "text-amber-700"}>{r.ok ? `enviado (${r.items})` : r.reason}</span></li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex justify-end mt-5">
            <Button onClick={save} disabled={saving} data-testid="save-settings-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Guardar cambios
            </Button>
          </div>
        )}
      </Card>

      {isAdmin && <UsersManager />}

      <CatalogEditor isAdmin={isAdmin} />

      {isAdmin && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <FileUp className="h-5 w-5 text-accent" />
            <h2 className="font-display text-lg font-bold">Importar plantilla desde Excel</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Sube un .xlsx con las columnas <span className="font-mono">DNI, Nombre, Apellidos, Teléfono, Email, Fecha alta, Fecha fin, Estado, Reconocimiento, Vence DNI, Cursos, Marca, Modelo, Etiqueta, Matrícula</span>.
            Los empleados se identifican por DNI: los existentes se actualizan y los nuevos se crean. Los cursos se desglosan en filas individuales.
          </p>
          <input ref={excelRef} type="file" accept=".xlsx" className="hidden" onChange={importExcel} data-testid="import-excel-input" />
          <Button onClick={() => excelRef.current?.click()} disabled={importing} data-testid="import-excel-button">
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}
            Subir Excel y actualizar plantilla
          </Button>
        </Card>
      )}

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <DatabaseBackup className="h-5 w-5 text-accent" />
          <h2 className="font-display text-lg font-bold">Copias de Seguridad</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Descarga en JSON o Excel (una hoja por sección: empleados, herramientas, vehículos, reconocimientos, cursos y EPIs). Nombre de archivo con formato <span className="font-mono">DD-MM-AA HH-MM</span>.
          Se genera una copia automática cada día.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => downloadBlob("/backup/json", "backup.json")}
            data-testid="download-backup-json-button">
            <FileJson className="h-4 w-4 mr-2" /> Descargar JSON
          </Button>
          <Button variant="outline" onClick={() => downloadBlob("/backup/xlsx", "backup.xlsx")}
            data-testid="download-backup-xlsx-button">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Exportar Excel (.xlsx)
          </Button>
          <Button variant="ghost" size="sm" onClick={() => downloadBlob("/backup/csv", "backup.csv")}
            data-testid="download-backup-csv-button">
            CSV
          </Button>
          {isAdmin && (
            <>
              <Button onClick={createBackup} disabled={busy} data-testid="create-backup-button">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DatabaseBackup className="h-4 w-4 mr-2" />}
                Guardar copia ahora
              </Button>
              <input ref={restoreRef} type="file" accept="application/json" className="hidden" onChange={restore} />
              <Button variant="outline" onClick={() => restoreRef.current?.click()} data-testid="restore-backup-button">
                <RotateCcw className="h-4 w-4 mr-2" /> Restaurar desde archivo
              </Button>
            </>
          )}
        </div>

        <div className="mt-6 border rounded-lg divide-y" data-testid="backups-list">
          {backups.length === 0 && <p className="p-4 text-sm text-muted-foreground text-center">Aún no hay copias guardadas.</p>}
          {backups.map((b) => (
            <div key={b.id} className="flex items-center justify-between p-3 gap-3">
              <div className="min-w-0">
                <p className="font-mono text-sm truncate">{b.filename}</p>
                <p className="text-xs text-muted-foreground">
                  {fmtDate(b.created_at)} · {b.employee_count} empleados
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={b.kind === "automatico" ? "bg-sky-100 text-sky-800 border border-sky-200" : "bg-slate-100 text-slate-700 border border-slate-200"}>
                  {b.kind === "automatico" ? "Automática" : "Manual"}
                </Badge>
                <Button size="icon" variant="ghost" onClick={() => downloadBlob(`/backups/${b.id}/download`, b.filename)}>
                  <Download className="h-4 w-4" />
                </Button>
                {isAdmin && (
                  <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => delBackup(b.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
